'use client';

import React from 'react';
import { DocShapeId } from '@/utils/shapeIds';
import { Bundle } from '@/ui/services/BundleService';
import { twJoin } from 'tailwind-merge';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import './styles.css';
import { BundleItemDetailsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import VariantCard from './components/variant-card/variant-card';
import ProductCard from './components/product-card/product-card';
import { ProductCardsTile } from './types/product-cards-tile';
import Container from '../../shared/Container';

// Resolves the responsive grid-column classes for the card layout. On mobile the cards always wrap
// into a grid (2 columns, or a single column for a lone card) so no variant is pushed off-screen;
// the sm/md overrides preserve the original per-count desktop layout.
function resolveGridColumnClasses(numberOfCards: number, areJustProducts: boolean): string {
  const mobileCols = numberOfCards === 1 ? 'grid-cols-1' : 'grid-cols-2';

  switch (numberOfCards) {
    case 2:
      return twJoin(mobileCols, 'sm:grid-cols-2 md:max-w-3xl md:mx-auto');
    case 3:
      return areJustProducts
        ? twJoin(mobileCols, 'sm:grid-cols-3')
        : twJoin(mobileCols, 'sm:grid-cols-3 md:max-w-5xl md:mx-auto');
    case 4:
      return areJustProducts ? mobileCols : twJoin(mobileCols, 'sm:grid-cols-2 md:grid-cols-4');
    default:
      return areJustProducts
        ? twJoin(mobileCols, 'sm:grid-cols-2 md:grid-cols-3')
        : twJoin(mobileCols, 'sm:grid-cols-2 md:grid-cols-4');
  }
}

export default function ProductCards({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ProductCardsTile;
  trackingPrefix?: string;
}>) {
  const products =
    tile.products?.items?.filter((product) => product.shape.identifier !== DocShapeId.bundle) || [];
  const variants = tile.products?.productVariants || [];
  const bundles =
    tile.products?.items?.filter((product) => product.shape.identifier === DocShapeId.bundle) || [];
  const areJustProducts = bundles.length === 0 && variants.length === 0;
  const numberOfCards = products.length + variants.length + bundles.length;
  const showSlider = (numberOfCards > 3 && areJustProducts) || numberOfCards > 4;
  const gridColumnClasses = resolveGridColumnClasses(numberOfCards, areJustProducts);

  return (
    <div className="overflow-hidden">
      <Container className="overflow-visible px-0">
        <div
          data-testid="product-cards-layout"
          className={twJoin(
            'grid gap-4 px-4 py-4 sm:gap-6 sm:px-0 sm:py-0 md:gap-8',
            gridColumnClasses,
            showSlider && 'sm:hidden',
          )}
        >
          {products?.map((product) => (
            <ProductCard
              key={`normal-product-card-${product.id}`}
              product={product}
              trackingPrefix={trackingPrefix}
            />
          ))}
          {bundles?.map((bundle) => {
            // assigning type since we verified the shape above
            const bundleInstance = new Bundle(bundle as BundleItemDetailsFragment);
            return (
              <VariantCard
                key={`bundle-card-${bundle.id}`}
                bundle={bundleInstance}
                trackingPrefix={trackingPrefix}
              />
            );
          })}
          {variants?.map((variant) => (
            <VariantCard
              key={`variant-card-${variant.sku}`}
              variant={variant}
              trackingPrefix={trackingPrefix}
            />
          ))}
        </div>
        <Swiper
          spaceBetween={16}
          slidesPerView={1.4}
          breakpoints={{
            640: {
              slidesPerView: 2.2,
              spaceBetween: 16,
            },
            1024: {
              slidesPerView: areJustProducts ? 3.2 : 4.2,
              spaceBetween: 32,
            },
          }}
          modules={[Navigation]}
          navigation
          className={twJoin('product-cards-slider hidden w-full', showSlider && 'sm:block')}
          style={showSlider ? {} : { display: 'none' }}
        >
          {products?.map((product) => (
            <SwiperSlide
              key={`normal-product-card-${product.id}`}
              className="rounded-3xl"
              style={{
                boxShadow:
                  '0px 0px 1px 0px rgba(33, 37, 41, 0.32), 0px 4px 16px 0px rgba(0, 0, 0, 0.1)',
              }}
            >
              <ProductCard product={product} trackingPrefix={trackingPrefix} />
            </SwiperSlide>
          ))}
          {bundles?.map((bundle) => {
            // assigning since we verified the shape above
            const bundleInstance = new Bundle(bundle as BundleItemDetailsFragment);
            return (
              <SwiperSlide key={`bundle-card-${bundle.id}`}>
                <VariantCard bundle={bundleInstance} trackingPrefix={trackingPrefix} />
              </SwiperSlide>
            );
          })}
          {variants?.map((variant) => (
            <SwiperSlide key={`variant-card-${variant.sku}`}>
              <VariantCard variant={variant} trackingPrefix={trackingPrefix} />
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>
    </div>
  );
}
